import { notifications } from '@mantine/notifications'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import type { GenerateFAQsResponse } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import type { FAQItem } from '@/types/faq'
import { isFAQModified } from '@/types/faq'

interface UseFAQManagementProps extends CMFormSection {
  type: 'cm' | 'step'
}

export interface FAQSectionRef {
  saveUnsavedFAQs: () => Promise<void>
}

export function useFAQManagement(props: UseFAQManagementProps) {
  // Get state and actions based on FAQ type
  const faqState = props.type === 'cm' ? props.cmFAQState : props.stepFAQState
  const actions = props.faqActions

  const setApprovedFAQs = props.type === 'cm' ? actions.setCMApprovedFAQs : actions.setStepApprovedFAQs
  const setPendingFAQs = props.type === 'cm' ? actions.setCMPendingFAQs : actions.setStepPendingFAQs
  const setShowValidationErrors =
    props.type === 'cm' ? actions.setCMShowValidationErrors : actions.setStepShowValidationErrors
  const setIsInitialized = props.type === 'cm' ? actions.setCMIsInitialized : actions.setStepIsInitialized

  const { approved: approvedFAQs, pending: pendingFAQs, showValidationErrors, isInitialized } = faqState

  const { getFieldState, generateFieldByName, rejectSuggestion } = useAIFieldGeneration(props)

  // Initialize from server data only when first loading or when explicitly resetting
  useEffect(() => {
    const existingFAQs = props.type === 'cm' ? props.cmFAQsQuery.data : props.stepFAQsQuery.data

    // Only initialize if we haven't initialized yet, or if we have no local changes
    const hasLocalChanges =
      approvedFAQs.some(
        faq =>
          faq.isNew ||
          faq.isPending ||
          faq.isDeleted ||
          (faq.originalQuestion !== undefined && faq.question !== faq.originalQuestion) ||
          (faq.originalAnswer !== undefined && faq.answer !== faq.originalAnswer),
      ) || pendingFAQs.length > 0

    if (!isInitialized && !hasLocalChanges) {
      if (existingFAQs) {
        const serverFAQs: FAQItem[] = existingFAQs.map(faq => ({
          id: faq.faqId,
          question: faq.question,
          answer: faq.answer,
          isNew: false,
          // Store original values to track modifications
          originalQuestion: faq.question,
          originalAnswer: faq.answer,
        }))
        setApprovedFAQs(serverFAQs)
        setPendingFAQs([])
      } else {
        setApprovedFAQs([])
        setPendingFAQs([])
      }
      setIsInitialized(true)
    }
  }, [
    props.type,
    props.cmFAQsQuery.data,
    props.stepFAQsQuery.data,
    isInitialized,
    setApprovedFAQs,
    setPendingFAQs,
    setIsInitialized,
  ])

  // Handle AI generation results
  useEffect(() => {
    const fieldState = getFieldState('faqs')
    if (fieldState.state === 'pending' && fieldState.suggestion) {
      try {
        const generatedFAQs = JSON.parse(fieldState.suggestion) as GenerateFAQsResponse['questions']
        const newPendingFAQs: FAQItem[] = generatedFAQs.map(faq => ({
          id: nanoid(),
          question: faq.question,
          answer: faq.answer,
          isNew: true,
          isPending: true,
        }))

        setPendingFAQs(prev => [...prev, ...newPendingFAQs])
        rejectSuggestion('faqs')
      } catch (error) {
        console.error('Failed to parse AI-generated FAQ suggestions:', error)
        notifications.show({
          title: 'Error',
          message: 'Failed to process AI-generated FAQs. Please try again.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        rejectSuggestion('faqs')
      }
    }
  }, [getFieldState, rejectSuggestion, setPendingFAQs])

  // Auto-generation logic - trigger when no FAQs exist and data is ready
  // Include server FAQs in the count to prevent auto-generation when opening existing CMs with FAQs
  const serverFAQs = props.type === 'cm' ? props.cmFAQsQuery.data : props.stepFAQsQuery.data
  const serverFAQCount = serverFAQs?.length ?? 0
  const totalFAQCount = approvedFAQs.length + pendingFAQs.length + serverFAQCount

  const shouldAutoGenerate = useMemo(() => {
    // Only auto-generate if we have no FAQs at all (local or server)
    if (totalFAQCount > 0) return false

    if (props.type === 'step') {
      // For steps: need step content to be available and successful
      return props.stepContentQuery?.isSuccess && !!props.stepContentQuery?.data
    } else {
      // For content modules: need steps to be available and CM query successful
      return props.stepsQuery?.isSuccess && props.cmQuery?.isSuccess && !!props.stepsQuery?.data?.length
    }
  }, [
    totalFAQCount,
    props.type,
    props.stepContentQuery?.isSuccess,
    props.stepContentQuery?.data,
    props.stepsQuery?.isSuccess,
    props.stepsQuery?.data?.length,
    props.cmQuery?.isSuccess,
  ])

  // Track auto-generation to prevent duplicates
  const autoGenerationTriggeredRef = useRef<string | null>(null)

  // Create a unique key for tracking auto-generation
  const generationKey = useMemo(() => {
    if (props.type === 'step') {
      return `step-${props.stepId}-${!!props.stepContentQuery?.data}`
    } else {
      return `cm-${props.taskId}-${!!props.stepsQuery?.data?.length}`
    }
  }, [props.type, props.stepId, props.taskId, props.stepContentQuery?.data, props.stepsQuery?.data?.length])

  useEffect(() => {
    if (shouldAutoGenerate && autoGenerationTriggeredRef.current !== generationKey) {
      autoGenerationTriggeredRef.current = generationKey
      generateFieldByName(props.type, 'faqs')
    }
  }, [shouldAutoGenerate, generationKey, generateFieldByName, props.type])

  // Reset auto-generation tracking when context changes
  useEffect(() => {
    if (props.type === 'step' && props.stepId) {
      // Reset when stepId changes to allow auto-generation for new steps
      autoGenerationTriggeredRef.current = null
      setIsInitialized(false)
    } else if (props.type === 'cm' && props.taskId) {
      // Reset when taskId changes to allow auto-generation for new content modules
      autoGenerationTriggeredRef.current = null
      setIsInitialized(false)
    }
  }, [props.stepId, props.taskId, props.type, setIsInitialized])

  const handleAddFAQ = useCallback(() => {
    setApprovedFAQs(prev => [
      {
        id: nanoid(),
        question: '',
        answer: '',
        isNew: true,
      },
      ...prev,
    ])
  }, [setApprovedFAQs])

  const handleUpdateFAQ = useCallback(
    (id: string, field: 'question' | 'answer', value: string) => {
      setApprovedFAQs(prev => prev.map(faq => (faq.id === id ? { ...faq, [field]: value } : faq)))
      setShowValidationErrors(false)
    },
    [setApprovedFAQs, setShowValidationErrors],
  )

  const handleDeleteFAQ = useCallback(
    async (id: string) => {
      // Mark the FAQ as deleted locally - don't call the API immediately
      setApprovedFAQs(prev => prev.map(faq => (faq.id === id ? { ...faq, isDeleted: true } : faq)))
      setPendingFAQs(prev => prev.filter(f => f.id !== id)) // Pending FAQs can be removed immediately since they're not saved
    },
    [setApprovedFAQs, setPendingFAQs],
  )

  const handleApproveFAQ = useCallback(
    (id: string) => {
      const faq = pendingFAQs.find(f => f.id === id)
      if (faq) {
        setPendingFAQs(prev => prev.filter(f => f.id !== id))
        setApprovedFAQs(prev => [...prev, { ...faq, isPending: false }])
      }
    },
    [pendingFAQs, setPendingFAQs, setApprovedFAQs],
  )

  const handleRejectFAQ = useCallback(
    (id: string) => {
      setPendingFAQs(prev => prev.filter(f => f.id !== id))
    },
    [setPendingFAQs],
  )

  const handleApproveAllFAQs = useCallback(() => {
    const approvedPendingFAQs = pendingFAQs.map(faq => ({ ...faq, isPending: false }))
    setApprovedFAQs(prev => [...prev, ...approvedPendingFAQs])
    setPendingFAQs([])
  }, [pendingFAQs, setApprovedFAQs, setPendingFAQs])

  const handleGenerateFAQs = useCallback(() => {
    generateFieldByName(props.type, 'faqs')
  }, [generateFieldByName, props.type])

  const saveUnsavedFAQs = useCallback(async () => {
    try {
      // Filter out deleted FAQs for processing
      const activeFAQs = approvedFAQs.filter(faq => !faq.isDeleted)
      const deletedFAQs = approvedFAQs.filter(faq => faq.isDeleted && !faq.isNew) // Only existing FAQs need server deletion

      // Separate new FAQs from existing FAQs that have been modified
      const newFAQs = activeFAQs.filter(faq => faq.isNew)
      const existingFAQs = activeFAQs.filter(faq => !faq.isNew)

      // Check for incomplete FAQs in all active FAQs (both new and existing)
      const allIncompleteFAQs = activeFAQs.filter(faq => {
        const hasQuestion = faq.question.trim().length > 0
        const hasAnswer = faq.answer.trim().length > 0
        return !(hasQuestion && hasAnswer) // Incomplete if either field is missing
      })

      // If there are incomplete FAQs, block the save
      if (allIncompleteFAQs.length > 0) {
        setShowValidationErrors(true)
        notifications.show({
          title: 'Cannot Save - Incomplete FAQs',
          message: `Please complete all FAQ fields before saving. ${allIncompleteFAQs.length} FAQ(s) are missing either a question or answer.`,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        throw new Error('FAQ_VALIDATION_ERROR')
      }

      // Get valid new FAQs (both question and answer filled)
      const validNewFAQs = newFAQs.filter(faq => faq.question.trim().length > 0 && faq.answer.trim().length > 0)

      // Get existing FAQs that have valid content (they should already be valid, but let's be safe)
      const validExistingFAQs = existingFAQs.filter(
        faq => faq.question.trim().length > 0 && faq.answer.trim().length > 0,
      )

      // Ensure we have required IDs for any operations
      if (validNewFAQs.length > 0 || validExistingFAQs.length > 0 || deletedFAQs.length > 0) {
        if (props.type === 'cm' && !props.taskId) {
          throw new Error('Task ID is required for content module FAQs')
        }
        if (props.type === 'step' && !props.stepId) {
          throw new Error('Step ID is required for step FAQs')
        }
      }

      const idParams = props.type === 'cm' ? { task_id: props.taskId! } : { step_id: props.stepId! }

      // Handle deleting FAQs first
      if (deletedFAQs.length > 0) {
        await props.faqsDeleteMutation?.mutateAsync({
          ...idParams,
          faq_ids: deletedFAQs.map(faq => faq.id),
        })
      }

      // Handle creating new FAQs
      if (validNewFAQs.length > 0) {
        await props.faqsCreateMutation?.mutateAsync({
          ...idParams,
          faqs: validNewFAQs.map(faq => ({
            question: faq.question.trim(),
            answer: faq.answer.trim(),
          })),
        })
      }

      // Handle updating existing FAQs
      if (validExistingFAQs.length > 0) {
        await props.faqsUpdateMutation?.mutateAsync({
          ...idParams,
          faqs: validExistingFAQs.map(faq => ({
            faq_id: faq.id, // Use the server ID for existing FAQs
            question: faq.question.trim(),
            answer: faq.answer.trim(),
          })),
        })
      }

      // Remove deleted FAQs from local state after successful server deletion
      if (deletedFAQs.length > 0) {
        setApprovedFAQs(prev => prev.filter(faq => !faq.isDeleted))
      }

      // Clear validation errors after successful save
      setShowValidationErrors(false)

      // Reset initialization state so future server updates can be applied
      setIsInitialized(false)
    } catch (error) {
      console.error('Failed to save FAQs:', error)
      throw error
    }
  }, [
    approvedFAQs,
    props.type,
    props.taskId,
    props.stepId,
    props.faqsCreateMutation,
    props.faqsUpdateMutation,
    props.faqsDeleteMutation,
  ])

  const resetFAQState = useCallback(() => {
    setApprovedFAQs([])
    setPendingFAQs([])
    setShowValidationErrors(false)
    setIsInitialized(false)
  }, [setApprovedFAQs, setPendingFAQs, setShowValidationErrors, setIsInitialized])

  // Derived state
  const hasUnsavedChanges = approvedFAQs.some(
    faq =>
      (faq.isNew && (faq.question.trim().length > 0 || faq.answer.trim().length > 0)) ||
      isFAQModified(faq) ||
      faq.isDeleted,
  )

  const hasIncompleteChanges = approvedFAQs.some(faq => {
    // Check both new FAQs and existing FAQs that are being displayed (not deleted)
    if (faq.isDeleted) return false
    const hasQuestion = faq.question.trim().length > 0
    const hasAnswer = faq.answer.trim().length > 0
    return !(hasQuestion && hasAnswer) // Incomplete if either field is missing
  })

  const isGenerating = getFieldState('faqs').state === 'loading'

  return {
    // State - filter out deleted FAQs from the UI
    approved: approvedFAQs.filter(faq => !faq.isDeleted),
    pending: pendingFAQs,
    showValidationErrors,
    isGenerating,
    hasUnsavedChanges,
    hasIncompleteChanges,
    totalCount: approvedFAQs.filter(faq => !faq.isDeleted).length + pendingFAQs.length,

    // Actions
    handleAddFAQ,
    handleGenerateFAQs,
    handleUpdateFAQ,
    handleDeleteFAQ,
    handleApproveFAQ,
    handleRejectFAQ,
    handleApproveAllFAQs,
    saveUnsavedFAQs,
    resetFAQState,
  }
}
